import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { z } from "zod";
import { DENSITY_CONFIG, LIST_ROW_PX, type Color, type WidgetListColumn } from "@salestrak/ui";
import { type WidgetDataSlice } from "#app/widgets/dashboards/widget-drilldown";
import {
  type OutcomeBreakdownRow,
  type OutcomeComparison,
  type OutcomeEntityRow,
  type OutcomePerformanceData,
} from "./use-outcome-performance";

export type OutcomeActivitySlice = Extract<WidgetDataSlice, { kind: "outcomeActivity" }>;

export const outcomePerformanceConfigSchema = z.looseObject({
  // "members" was its own view — a table of members — until the summary
  // grew the member list; a stored one reads as the summary it became.
  view: z
    .preprocess(
      (view) => (view === "members" ? "summary" : view),
      z.enum(["summary", "entities"]),
    )
    .default("summary"),
  sortBy: z.enum(["updates", "positive-rate", "completion"]).default("updates"),
  minUpdates: z.int().min(1).max(100).default(1),
  limit: z.int().min(3).max(20).default(8),
});

export type OutcomePerformanceConfig = z.infer<
  typeof outcomePerformanceConfigSchema
>;

/** The outcome distribution — a closed set, so a zero tile still renders: no
 * negatives is the good-news reading, and pinning all three keeps the tile row
 * a stable width as the data thins. The summary band is exactly these three:
 * three tiles fit one row at half width, and a fourth folds the band into a
 * 2×2 with a rule stranded down its middle. */
export const DISTRIBUTION_TONES = ["positive", "neutral", "negative"] as const;

/** `missing` is not a member of that distribution — it is the data-quality
 * exception. On the summary it is never a tile: the tile shares are of the
 * Updates that HAVE an outcome (so they sum to 100), and the outcome-less
 * ones stand under the band as one segment of the distribution bar and one
 * pressable row, present only while they have a count. That covers the
 * "Require outcome" workspace with nothing to configure and no settings read:
 * Updates posted before the rule keep their row until they leave the window,
 * and every window after it shows the three tiles alone. */
export const TONE_ORDER = [...DISTRIBUTION_TONES, "missing"] as const;

export type ToneKey = (typeof TONE_ORDER)[number];

/** Which tone bands the breakdown table renders. The distribution is always
 * all three; `missing` joins them only when it has a count. */
export function summaryTones(missing: number): readonly ToneKey[] {
  return missing > 0 ? TONE_ORDER : DISTRIBUTION_TONES;
}

/**
 * Updates with an outcome below which a share is printed as a fraction rather
 * than a percentage. "67% positive" from three events is a claim the data
 * cannot carry; "2 of 3" says exactly what happened.
 */
export const RATE_SAMPLE_FLOOR = 5;

/**
 * A tile's caption: the tone's share of the Updates that have an outcome, or
 * the fraction while too few do, or a dash where the tone counted nothing.
 */
export function tileCaption(count: number, completed: number): string {
  if (count === 0 || completed === 0) return "—";
  if (completed < RATE_SAMPLE_FLOOR) return `of ${completed.toLocaleString()}`;
  return formatShare(count, completed);
}

/**
 * A share, or the fraction while the base is too small to carry a rate, or a
 * dash where there is no base at all. The one rule every figure on the card
 * reads — the header's rate, the tone legend, each member's rate.
 */
export function shareFigure(count: number, base: number): string {
  if (base === 0) return "—";
  if (base < RATE_SAMPLE_FLOOR) return `${count.toLocaleString()} of ${base.toLocaleString()}`;
  return formatShare(count, base);
}

/**
 * The positive rate as the header reads it, beside the Update count: "63%
 * positive", or the fraction while too few Updates carry a rate. Null where
 * no Update has an outcome — the missing row under the bar already says so,
 * and a dash beside the count would restate it.
 */
export function rateText(
  data: Pick<OutcomePerformanceData, "positive" | "completed">,
): string | null {
  if (data.completed === 0) return null;
  return `${shareFigure(data.positive, data.completed)} positive`;
}

/**
 * The previous window's positive rate beside its name — "vs 52% last week".
 * Two figures, never a delta: the metric and stat strip read theirs the same
 * way, and a delta over a handful of Updates claims a trend the data cannot.
 */
export function comparisonText(comparison: OutcomeComparison): string {
  if (comparison.completed === 0) return `None ${comparison.basis}`;
  return `vs ${shareFigure(comparison.positive, comparison.completed)} ${comparison.basis}`;
}

/** Below this body width the outcome words under each tone drop and the
 *  member name column yields. */
export const COMPACT_MAX_WIDTH = 560;

/**
 * The summary folds by HEIGHT (width only thins it), and it folds by what
 * FITS rather than by one round threshold: a card that holds the legend
 * shows it, whatever the number. The mix's rungs, top down — bar, legend
 * (label / count / outcome words), the missing row — are summed from their
 * parts here so the fold never hides a line the body had room for.
 *
 * - `full`: bar, legend with outcome words, missing row
 * - `words-off`: the words drop first — detail, not the answer
 * - `legend`: the missing row drops next — its yellow segment stays in the bar
 * - `bar`: the bar alone
 *
 * From `listMinHeight` the list under the mix (members, or outcomes on a
 * member's board) takes what remains and scrolls. The card's height is the
 * board's — content folds to fit it, never grows it.
 */
export type MixFold = "full" | "words-off" | "legend" | "bar";

/** The `sm` gap between the mix's rungs. */
const MIX_GAP_PX = 8;
/** A legend tile: `xs` padding ×2, the 16px label line, a `2xs` gap, the
 *  `lg` count line at tight leading (18 × 1.25 ≈ 23). The outcome words add
 *  another gap and a 16px line. */
const LEGEND_PX = 4 * 2 + 16 + 2 + 23;
const LEGEND_WORDS_PX = 2 + 16;
/** The missing row: `xs` padding ×2 around one 16px line. */
const MISSING_ROW_PX = 4 * 2 + 16;

/** The mix's height at each fold, for a window with or without a missing
 *  row. The bar's own thickness is `DISTRIBUTION_BAR_PX`. */
export function mixHeight(fold: MixFold, hasMissing: boolean): number {
  if (fold === "bar") return DISTRIBUTION_BAR_PX;
  const legend = DISTRIBUTION_BAR_PX + MIX_GAP_PX + LEGEND_PX;
  if (fold === "legend") return legend;
  const missing = hasMissing ? MIX_GAP_PX + MISSING_ROW_PX : 0;
  if (fold === "words-off") return legend + missing;
  return legend + LEGEND_WORDS_PX + missing;
}

/** The fullest fold that fits the measured body height. */
export function mixFold(bodyHeight: number, hasMissing: boolean): MixFold {
  for (const fold of ["full", "words-off", "legend"] as const) {
    if (bodyHeight >= mixHeight(fold, hasMissing)) return fold;
  }
  return "bar";
}

/** The list's chrome over its rows: the separator with its `md` margins
 *  (16 + 1 + 16) and the 24px column header. */
const LIST_CHROME_PX = 16 + 1 + 16 + 24;
/** The fewest rows a list is worth: one row under a header reads as a
 *  stray line, two read as a list. */
const LIST_MIN_ROWS = 2;

/** The body height from which the list under the mix is drawn: the full mix,
 *  the list's chrome and two rows. Under it the mix stands alone. */
export function listMinHeight(hasMissing: boolean): number {
  return mixHeight("full", hasMissing) + LIST_CHROME_PX + LIST_MIN_ROWS * LIST_ROW_PX;
}

/** The member row's figure columns, in px: the Updates count, then the rate
 *  of whichever tone the card is focused on. */
export const MEMBER_COUNT_PX = 40;
export const MEMBER_RATE_PX = 64;
/**
 * The member name's column. One fixed width, so every member bar starts on
 * one left edge and the eye runs straight down it; wide enough for a full
 * name beside its avatar, no wider, so the bars keep the card. Compact
 * yields the column to keep the bar's room.
 */
export const MEMBER_NAME_PX = 200;
export const MEMBER_NAME_COMPACT_PX = 128;
/** The member bar's thickness — a mark per row, lighter than the card's bar. */
export const MEMBER_BAR_PX = 8;
/** The card's own distribution bar. */
export const DISTRIBUTION_BAR_PX = 12;

export const TONE_LABELS: Record<ToneKey, string> = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
  missing: "No outcome",
};

export const TONE_COLORS: Record<ToneKey, Color> = {
  positive: "green",
  neutral: "grey",
  negative: "red",
  missing: "yellow",
};

/** The body width past which the member table's second column and the
 *  summary's side-by-side band fit; under it the summary folds compact. */
export const WIDE_BODY_PX = 640;

/**
 * The member row's figure columns — named ONCE in the list header, so a row
 * carries figures alone on one line. Each survives down to a body width;
 * below it the column drops (Completed first, then Positive), Updates stays.
 * Measured off the body, never the grid span (see SPLIT rationale above).
 */
export const MEMBER_COLUMNS = [
  { key: "updates", label: "Updates", width: 64, minWidth: 0 },
  { key: "positive", label: "Positive", width: 88, minWidth: 420 },
  { key: "completed", label: "Completed", width: 88, minWidth: 700 },
] as const satisfies readonly WidgetListColumn[];
export type MemberColumn = (typeof MEMBER_COLUMNS)[number];


/** Placeholder rows before the first answer, when nothing yet says how many
 *  members will rank. Three: the shortest run that still reads as a list. */
export const FIRST_PAINT_MEMBER_ROWS = 3;

/**
 * What a press on the summary or the member list opened: one tone's Updates
 * or one member's. Held as the CHOICE rather than the slice it resolves to, so
 * the slice — its outcome ids, its count — is re-read from whatever the data
 * says now: a window change re-queries the card, and the open panel follows
 * the new answer instead of paging the old one.
 */
export type OutcomeSelection = { tone: ToneKey } | { outcomeId: string } | { actorId: string };

export function sameSelection(a: OutcomeSelection | null, b: OutcomeSelection): boolean {
  if (!a) return false;
  if ("tone" in a) return "tone" in b && a.tone === b.tone;
  if ("outcomeId" in a) return "outcomeId" in b && a.outcomeId === b.outcomeId;
  return "actorId" in b && a.actorId === b.actorId;
}


/** A tone with no outcome words behind it: one stable empty set, so its local
 *  group provider never sees a fresh array and re-pages for nothing. */
export const EMPTY_OUTCOMES: OutcomeBreakdownRow[] = [];

/** The breakdown table's geometry: the tone band is the column header's 32px
 *  band, and each group closes with the table's default `md` trailing gap. */
export const BREAKDOWN_BAND_HEIGHT = DENSITY_CONFIG.compact.headerHeight;
export const BREAKDOWN_GROUP_GAP = 16;
/** Outcome rows the reserve grows to before the table scrolls instead — the
 *  half row is the peek that says there is more below. */
export const BREAKDOWN_MAX_ROWS = 12.5;

/**
 * The height the disclosed breakdown table reserves, computed from the whole
 * window rather than the open entity row: every outcome word any kind used
 * plus the No outcome band when any kind has one. So the reserve is the same
 * for every kind on the card — opening one and then another moves nothing on
 * the board — and it fits the full vocabulary, so the common case never
 * scrolls. Only a window or filter change re-derives it, and that re-queries
 * the card anyway.
 */
export function breakdownReserveHeight(rows: readonly OutcomeEntityRow[]): number {
  const outcomeIds = new Set<string>();
  let missing = 0;
  for (const row of rows) {
    for (const outcome of row.outcomes) {
      outcomeIds.add(outcome.outcomeId);
      if (outcome.tone === "missing") missing += outcome.count;
    }
  }
  const bands = summaryTones(missing).length;
  const dataRows = Math.min(Math.max(outcomeIds.size, 1), BREAKDOWN_MAX_ROWS);
  const density = DENSITY_CONFIG.compact;
  return (
    density.headerHeight +
    bands * (BREAKDOWN_BAND_HEIGHT + BREAKDOWN_GROUP_GAP) +
    dataRows * density.rowHeight
  );
}

export function updatesNoun(count: number): string {
  return count === 1 ? "Project Update" : "Project Updates";
}

/**
 * The card's totals line: the volume. The header's adornment never shrinks —
 * the TITLE yields to it — and the card cannot measure the header from the
 * body, so the line stays short: the rate joins it (`rateText`) as a few
 * words, the previous window waits for the default width, and the tone
 * shares stay in the body, where the width IS measured.
 */
export function totalsText(data: Pick<OutcomePerformanceData, "total">): string {
  return `· ${data.total.toLocaleString()} ${data.total === 1 ? "Update" : "Updates"}`;
}
